import { MutableRefObject, PropsWithChildren, ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CardVariant } from "../../../models/CardVariant";
import { Spinner } from "../../Spinner/Spinner";
import ItemDisplayToolbar from "../ItemDisplayToolbar";
import FullScreenModal from "../../Modal/FullScreenModal";
import CardDisplayQuickResults from "./CardDisplayQuickResults";
import { calculateNumberOfFiltersApplied } from "../functions";
import ItemFilterModal from "../ItemFilterModal";
import CardFilterModalBody, { CardFilterModalBodyOptionalProps } from "./CardFilterModalBody";
import { ItemDisplay, ITEM_DISPLAY_STYLES } from "../ItemDisplay";
import { UserSettings, getDefaultUserSettings } from "../../../models/UserSettings";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { DeckBuilderFilters } from "./DeckBuilderFilters";
import { classNames } from "../../../style/common";
import { AppState, VariantCache, getDefaultVariantCache } from "../../../appSlice";
import { UserState } from "../../../state/userSlice";
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CardFilterState, filterCards, getDefaultCardFilterState } from "../../../functions/filter/CardFilterCommands";

export type DeckBuilderCardSearchContextState = {
    type: "deck" | "tournament" | "trade",
    allItems: MutableRefObject<CardVariant[]>,
    filteredItems: CardVariant[],
    setFilteredItems: (items: CardVariant[]) => void,
    itemsDisplayed: number,
    setItemsDisplayed: (itemsDisplayed: number) => void,
    batchSize: number,
    userSettings: UserSettings,
    isCardSearchOpen: boolean,
    setIsCardSearchOpen: (isOpen: boolean) => void,
    isCardSearchFilterModalOpen: boolean,
    setIsCardSearchFilterModalOpen: (isOpen: boolean) => void,
    getDefaultFilters: MutableRefObject<() => CardFilterState>,
    filters: CardFilterState,
    setFilters: (filters: CardFilterState) => void,
    collection: Record<string, number>,
    variantCache: VariantCache,
    filter: MutableRefObject<FilterFunction>,
    setFilter: (newFunction: FilterFunction) => void,
    showVariants: boolean,
    setShowVariants: (value: boolean) => void
}

export type FilterFunction = (filters: CardFilterState, isSearchTargetRemove?: boolean, scrollTo?: "searchTargetNotification" | "fullScreenSearchResults" | "searchResults") => void

export const DeckBuilderCardSearchContext = createContext<DeckBuilderCardSearchContextState>({
    type: "deck",
    allItems: { current: [] },
    filteredItems: [],
    setFilteredItems: () => { },
    itemsDisplayed: 48,
    setItemsDisplayed: () => { },
    batchSize: 48,
    isCardSearchOpen: false,
    setIsCardSearchOpen: () => { },
    isCardSearchFilterModalOpen: false,
    setIsCardSearchFilterModalOpen: () => { },
    userSettings: getDefaultUserSettings(),
    getDefaultFilters: { current: () => getDefaultCardFilterState({ userSettings: getDefaultUserSettings(), primarySerialOnly: false }) },
    filters: getDefaultCardFilterState({ userSettings: getDefaultUserSettings(), primarySerialOnly: false }),
    setFilters: () => { },
    collection: {},
    variantCache: getDefaultVariantCache(),
    filter: { current: () => { } },
    setFilter: () => { },
    setShowVariants: () => { },
    showVariants: false
});

export function BuilderCardSearchContextProvider(props: PropsWithChildren & {
    type: "deck" | "tournament" | "trade"
}) {
    const { userSettings } = useSelector<RootState, UserState>(x => x.user);
    const { collection, variantCache } = useSelector<RootState, AppState>(x => x.app);
    const [filteredItems, setFilteredItems] = useState<CardVariant[]>([]);
    const [filters, setFilters] = useState<CardFilterState>(getDefaultCardFilterState({ userSettings: userSettings, primarySerialOnly: false }));
    const [isCardSearchFilterModalOpen, setIsCardSearchFilterModalOpen] = useState<boolean>(false);
    const [isCardSearchOpen, setIsCardSearchOpen] = useState<boolean>(false);
    const [itemsDisplayed, setItemsDisplayed] = useState<number>(48);
    const [showVariants, setShowVariants] = useState<boolean>(false);
    const batchSize = 48;

    const filter = useRef<FilterFunction>(() => { });
    const setFilter = (newFunction: FilterFunction) => {
        filter.current = newFunction;
    }

    const getDefaultFilters = useRef<() => CardFilterState>(() => getDefaultCardFilterState({
        userSettings: userSettings,
        primarySerialOnly: false
    }));

    const allItems = useRef<CardVariant[]>([]);

    useEffect(() => {
        getDefaultFilters.current = () => getDefaultCardFilterState({
            userSettings: userSettings,
            primarySerialOnly: props.type === "deck" && userSettings.primarySerialOnly,
            defaultShowVariants: props.type === "deck" && userSettings.deckBuilderAllVariants
        });

        setFilters(getDefaultFilters.current());
    }, [userSettings]);

    useEffect(() => {
        let newVariants: CardVariant[];

        switch (props.type) {
            case "deck":
                if (showVariants) {
                    newVariants = JSON.parse(JSON.stringify(variantCache.unique.playable.list));
                } else {
                    newVariants = JSON.parse(JSON.stringify(variantCache.primary.playable.list));
                }

                break;
            case "tournament":
                newVariants = JSON.parse(JSON.stringify(variantCache.primary.playable.list));
                break;
            case "trade":
                newVariants = JSON.parse(JSON.stringify(variantCache.any.all.list));
                break;
            default:
                newVariants = JSON.parse(JSON.stringify(variantCache.any.all.list));
                break;
        }

        allItems.current = newVariants;
    }, [variantCache, showVariants]);

    return (
        <DeckBuilderCardSearchContext.Provider value={{
            type: props.type,
            allItems: allItems,
            filteredItems: filteredItems,
            setFilteredItems: setFilteredItems,
            batchSize: batchSize,
            itemsDisplayed: itemsDisplayed,
            setItemsDisplayed: setItemsDisplayed,
            filters: filters,
            setFilters: setFilters,
            getDefaultFilters: getDefaultFilters,
            isCardSearchFilterModalOpen: isCardSearchFilterModalOpen,
            setIsCardSearchFilterModalOpen: setIsCardSearchFilterModalOpen,
            isCardSearchOpen: isCardSearchOpen,
            setIsCardSearchOpen: setIsCardSearchOpen,
            userSettings: userSettings,
            collection: collection,
            variantCache: variantCache,
            filter: filter,
            setFilter: setFilter,
            setShowVariants: setShowVariants,
            showVariants: showVariants
        }}>
            {props.children}
        </DeckBuilderCardSearchContext.Provider>
    )
}

export type DeckBuilderCardDisplayProps<TItem extends CardVariant> = {
    onItemClick: (item: CardVariant) => void,
    showOptionalFilters?: boolean,
    itemComponentGenerator: (item: TItem, index: number) => ReactNode,
    classNames?: {
        container?: string | undefined,
        cardResults?: string | undefined
    }
}

export default function DeckBuilderCardSearch(props: DeckBuilderCardDisplayProps<CardVariant>) {
    const {
        allItems,
        filteredItems,
        setFilteredItems,
        itemsDisplayed,
        setItemsDisplayed,
        batchSize,
        userSettings,
        isCardSearchOpen,
        setIsCardSearchOpen,
        isCardSearchFilterModalOpen,
        setIsCardSearchFilterModalOpen,
        getDefaultFilters,
        filters,
        setFilters,
        collection,
        setFilter,
        filter,
        variantCache,
        type
    } = useContext(DeckBuilderCardSearchContext);

    const fullScreenSearchResultsRef = useRef<HTMLDivElement>(null);
    const searchTargetsRef = useRef<HTMLDivElement>(null);
    const [quickSearchItems, setQuickSearchItems] = useState<CardVariant[]>([]);
    const [numberOfFiltersApplied, setNumberOfFiltersApplied] = useState<number>(0);
    const resultsRef = useRef<HTMLDivElement>(null);

    const horizontalScroll = () => {
        document.getElementById("horizontal-scroll-container")?.scrollTo({ left: 0 });
    }

    const resetResults = () => {
        const newFilters = getDefaultFilters.current();
        newFilters.searchTargetRule = filters.searchTargetRule;

        setItemsDisplayed(batchSize);
        setFilters(newFilters);
        setFilteredItems(filterCards(allItems.current, newFilters, { collection: collection }));

        fullScreenSearchResultsRef?.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
        horizontalScroll();
    }

    const onShowMorePress = () => {
        setItemsDisplayed(itemsDisplayed + batchSize);
    }

    const onSearchTextChange = (newText: string) => {
        const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
        newFilters.searchText = newText;
        setFilters(newFilters);
    }

    const scrollToResultsIfNeeded = () => {
        if (!resultsRef?.current) {
            return;
        }

        const rectangle = resultsRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        // Already visible
        if (rectangle.top >= 0 && rectangle.bottom <= viewportHeight) {
            return;
        }

        const scrollY = window.scrollY || window.pageYOffset;
        const targetScroll = scrollY + rectangle.top + 300 - viewportHeight;

        // Scroll so that the bottom of the element aligns with the bottom of the viewport
        window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }

    useEffect(() => {
        setIsCardSearchFilterModalOpen(false);
        setIsCardSearchOpen(false);
    }, [userSettings.deckFullScreenCardSearch]);

    useEffect(() => {
        setNumberOfFiltersApplied(
            calculateNumberOfFiltersApplied(
                filters,
                getDefaultFilters.current()
            )
        );
    }, [filters]);

    const filterFunction = useCallback<FilterFunction>((filterState, isSearchTargetRemove, scrollTo) => {
        if (!filterState) {
            return;
        }

        setItemsDisplayed(batchSize);
        setFilters(filterState);
        setFilteredItems(filterCards(allItems.current, filterState, { collection: collection }));

        if (userSettings.deckFullScreenCardSearch) {
            setIsCardSearchFilterModalOpen(false);
        }

        if (!isSearchTargetRemove) {
            setIsCardSearchOpen(true);
        }

        switch (scrollTo) {
            case undefined:
                break;
            case "fullScreenSearchResults":
                fullScreenSearchResultsRef?.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
                break;
            case "searchTargetNotification":
                if (!searchTargetsRef?.current) {
                    break;
                }

                const offset = -75;
                const elementPosition = searchTargetsRef.current.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.scrollY + offset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
                break;
            case "searchResults":
                scrollToResultsIfNeeded();
                break;
        }

        horizontalScroll();
    }, [allItems.current, userSettings, fullScreenSearchResultsRef, searchTargetsRef]);

    useEffect(() => {
        setFilter(filterFunction);
    }, [filterFunction]);

    useEffect(() => {
        if (isCardSearchOpen && fullScreenSearchResultsRef?.current) {
            fullScreenSearchResultsRef?.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
            horizontalScroll();
        }
    }, [isCardSearchOpen]);

    if (!allItems?.current?.length) {
        return <Spinner />
    }

    return (
        <>
            <div className={classNames(props.classNames?.container)}>
                <div className={`max-w-6xl xl:m-auto flex flex-col select-none items-center justify-between relative`}>
                    <div id="search-target-scroll-target" ref={searchTargetsRef}></div>

                    {
                        filters.searchTargetRule &&
                        <div className="w-full mb-3 rounded min-h-[34px] overflow-hidden border border-color-border/40 flex items-stretch">
                            <div className="bg-info px-3 py-2 flex-1 break-words whitespace-break-spaces flex items-center gap-2">
                                <FunnelIcon className="h-4 w-4" />
                                <div className="leading-3 flex flex-col md:flex-row md:gap-1">
                                    <div className="text-sm font-medium">Search Targets For</div>
                                    <div className="text-sm">
                                        <span className="whitespace-nowrap">{filters.searchTargetRule?.variant?.serial}:</span> {filters.searchTargetRule?.variant?.card?.name}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="flex items-center px-3 cursor-pointer bg-info hover:bg-error-secondary hover:text-error-contrast"
                                onClick={() => {
                                    const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                                    newFilters.searchTargetRule = null;
                                    setFilters(newFilters);
                                    filter.current(newFilters, true);
                                }}
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </div>
                        </div>
                    }

                    <CardDisplayQuickResults
                        hideNumberOfFiltersApplied={userSettings.deckFullScreenCardSearch}
                        hideResetButton={true}
                        numberOfFiltersApplied={numberOfFiltersApplied}
                        items={quickSearchItems}
                        onTypeStop={(newSearchText) => {
                            const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                            newFilters.searchText = newSearchText;
                            setFilters(newFilters);

                            let newItems = filterCards(allItems.current, newFilters, { collection: collection });
                            newItems = newItems.slice(0, batchSize);

                            if (newSearchText) {
                                setQuickSearchItems(newItems);
                            } else {
                                setQuickSearchItems([]);
                            }
                        }}
                        searchText={filters.searchText || ""}
                        onTextChange={(newText) => {
                            const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                            newFilters.searchText = newText;
                            if (!!newText) {
                                newFilters.nameText = "";
                                newFilters.effectText = "";
                            }

                            setFilters(newFilters);
                        }}
                        onOptionClick={(item) => props.onItemClick(item)}
                        onEnterPress={(newSearchText) => {
                            const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                            newFilters.searchText = newSearchText;
                            setFilters(newFilters);
                            setItemsDisplayed(batchSize);
                            setFilteredItems(filterCards(allItems.current, newFilters, { collection: collection }));

                            if (userSettings.deckFullScreenCardSearch) {
                                setIsCardSearchFilterModalOpen(false);
                            }

                            setIsCardSearchOpen(true);

                            if (userSettings.deckFullScreenCardSearch) {
                                fullScreenSearchResultsRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
                            } else {
                                scrollToResultsIfNeeded();
                            }
                            horizontalScroll();
                        }}
                        onSearchTextChange={(newText) => onSearchTextChange(newText)}
                        onFiltersClick={() => {
                            if (userSettings.deckFullScreenCardSearch) {
                                setIsCardSearchOpen(true);
                            }

                            setIsCardSearchFilterModalOpen(!isCardSearchFilterModalOpen);
                        }}
                        onResetClick={() => resetResults()}
                        onSearchTextEnterKey={(searchText) => {
                            const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                            newFilters.searchText = searchText;
                            filter.current(newFilters);
                        }}
                        tooltipPosition="relative"
                        isOpen={true} />
                </div>
            </div>

            {
                userSettings.deckFullScreenCardSearch
                    ? <FullScreenModal
                        isOpen={isCardSearchOpen}
                        onClose={() => {
                            setIsCardSearchOpen(false);
                            resetResults();
                        }}
                        title="Add Cards to Deck">
                        <div className="absolute inset-0">
                            <ItemDisplayToolbar
                                numberOfFiltersApplied={numberOfFiltersApplied}
                                placeholderText="Search Cards"
                                tooltipPosition="relative"
                                classNames={{
                                    backgroundContainer: "!mx-0",
                                    container: "!mx-0 top-[60px] border-t border-t-gray-700"
                                }}
                                searchText={filters.searchText || ""}
                                onSearchTextChange={(newText) => onSearchTextChange(newText)}
                                onFiltersClick={() => setIsCardSearchFilterModalOpen(true)}
                                onResetClick={() => resetResults()}
                                onSearchTextEnterKey={(searchText) => {
                                    const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                                    newFilters.searchText = searchText;
                                    filter.current(newFilters);
                                }} />

                            <div className="absolute top-[117px] bottom-0 left-0 right-0 !overflow-y-scroll p-4" ref={fullScreenSearchResultsRef}>
                                <ItemDisplay
                                    classNames={{
                                        itemsContainer: ITEM_DISPLAY_STYLES.default
                                    }}
                                    items={filteredItems}
                                    itemsDisplayed={itemsDisplayed}
                                    onShowMoreClick={() => onShowMorePress()}
                                    itemComponentGenerator={(item, index) => props.itemComponentGenerator(item, index)}
                                />
                            </div>
                        </div>

                        <ItemFilterModal
                            currentFilterState={filters}
                            ignoreNavbarDodge={true}
                            isOpen={isCardSearchFilterModalOpen}
                            modalBody={CardFilterModalBody}
                            modalBodyOptionalProps={{
                                collection: collection,
                                showCollectionFilter: props.showOptionalFilters,
                                showAllVariantsFilter: true,
                                showVariantTypeFilter: props.showOptionalFilters
                            } as CardFilterModalBodyOptionalProps}
                            getDefaultFilterState={getDefaultFilters.current}
                            onOpenChange={(newIsOpen) => setIsCardSearchFilterModalOpen(newIsOpen)}
                            onApplyFilters={(filterState) => {
                                filter.current(filterState)
                            }} />
                    </FullScreenModal>
                    : <>
                        <div className={
                            classNames(
                                "flex justify-center mt-3",
                                isCardSearchFilterModalOpen ? "" : "hidden"
                            )}
                        >
                            <DeckBuilderFilters
                                filterState={filters}
                                onApplyFilters={() => {
                                    filter.current(filters, undefined, "searchResults");
                                }}
                                showVariantsFilter={type === "deck" || type === "trade"}
                                onReset={() => resetResults()}
                                collection={collection}
                                showVariantTypeFilter={props.showOptionalFilters}
                                updateState={(updateFunction: (newFilterState: CardFilterState) => void) => {
                                    const newFilters: CardFilterState = JSON.parse(JSON.stringify(filters));
                                    updateFunction(newFilters);
                                    setFilters(newFilters);
                                }} />
                        </div>

                        <div ref={resultsRef}>
                            {
                                isCardSearchOpen &&
                                <ItemDisplay
                                    classNames={{
                                        container: "select-none pb-2",
                                        itemsContainer: classNames(ITEM_DISPLAY_STYLES.horizontal, props.classNames?.cardResults)
                                    }}
                                    displayContainerId="horizontal-scroll-container"
                                    items={filteredItems}
                                    itemsDisplayed={itemsDisplayed}
                                    onShowMoreClick={() => onShowMorePress()}
                                    itemComponentGenerator={(item, index) => props.itemComponentGenerator(item, index)} />
                            }
                        </div>
                    </>
            }
        </>
    )
}